import React, { useRef, useState } from 'react';
import { studentApi } from '../services/api';

/**
 * Sınıflarım with no class yet — the "empty seat" card.
 *
 * It is the class card itself (the same tiling header and body as the grid's cards),
 * with the class code field where the class name will be: the one thing a student here
 * can do is type the code their teacher gave them, so the field is on the page, not
 * behind a button. Beside it, what joining turns on — named by the menu items the
 * student already sees, with their own sidebar icons and colours.
 *
 * The mobile twin is dijipo-mobile/components/classes/JoinClassCard.tsx; keep the copy
 * in step.
 */

const F = { fontFamily: 'Inter' } as const;

// utils/class-codes.ts mints every class code as 8 characters from an alphabet without
// I, L, O, 0 and 1. The field takes any letter or digit (a pasted "ABCD-EFGH" works) and
// leaves the final word to the server.
const CODE_LENGTH = 8;
const sanitize = (raw: string) => raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH);

const UNLOCKS = [
  { icon: '/sidebar-icons/randevularim.svg', color: '#6d399a', title: 'Randevularım', text: 'Öğretmenlerini görür, onlardan randevu alırsın.' },
  { icon: '/sidebar-icons/odevlerim.svg', color: '#f87aff', title: 'Ödevlerim', text: 'Öğretmeninin bundan sonra verdiği ödevler burada toplanır.' },
  { icon: '/sidebar-icons/calisma-planim.svg', color: '#6497d2', title: 'Çalışma Planım', text: 'Sınıfına bundan sonra verilen hedefler planına eklenir.' },
];

const JoinClassCard: React.FC<{
  kurumName?: string | null;
  /** Kurumlu but not placed (class_status 'awaiting'): the note names the second path. */
  awaiting: boolean;
  onJoined: (className: string | null) => void;
}> = ({ kurumName, awaiting, onJoined }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const complete = code.length === CODE_LENGTH;

  const submit = async () => {
    if (!complete || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await studentApi.joinClass(code);
      onJoined(res?.data?.className ?? null);
    } catch (e: any) {
      // The backend answers every refusal (wrong code, full class, already a member)
      // with 400 { error }; anything else — a dropped connection too — is not the
      // student's code being wrong, so it must not say so.
      setError(e?.response?.status === 400
        ? (e?.response?.data?.error ?? 'Geçersiz sınıf kodu. Lütfen kontrol edip tekrar deneyin.')
        : 'Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,520px)_minmax(0,1fr)] lg:items-start">
      {/* ── The empty seat: a class card waiting for its class ─────────────── */}
      <div className="bg-[#fafafa] rounded-[20px] overflow-hidden" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
        <div className="relative h-[103px] overflow-hidden">
          <img src="/a-sinif-tiling1.png" alt="" className="w-full h-full object-cover object-center" />
          {kurumName ? (
            <span className="absolute left-4 bottom-3 rounded-full bg-white/90 px-3 py-1" style={{ ...F, fontSize: 13, fontWeight: 600, color: '#3973bf' }}>
              {kurumName}
            </span>
          ) : null}
        </div>

        <div className="p-5">
          <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#2b2b2b' }}>Sınıf kodunla katıl</h2>
          <p style={{ ...F, fontSize: 14, color: '#666', marginTop: 4 }}>Öğretmeninin verdiği 8 karakterli kodu gir.</p>

          {/* 8 slots over one real input: the slots show the code; the input takes the
              typing, a paste, the keyboard focus and the screen reader. */}
          <div
            className="relative mt-4 rounded-[14px] focus-within:ring-2 focus-within:ring-[#3973bf]/40 focus-within:ring-offset-2"
            onClick={() => inputRef.current?.focus()}
          >
            <div className="flex gap-1 min-[360px]:gap-1.5 sm:gap-2" aria-hidden="true">
              {Array.from({ length: CODE_LENGTH }, (_, i) => {
                const ch = code[i];
                const active = focused && !submitting && i === Math.min(code.length, CODE_LENGTH - 1);
                const tone = error
                  ? 'border-[#e5599b] bg-[#fdeef5]'
                  : active
                    ? 'border-[#3973bf] bg-[#eaf1fb]'
                    : ch
                      ? 'border-[#3973bf] bg-white'
                      : 'border-[#d5dcea] bg-white';
                return (
                  <div key={i} className={`relative flex-1 min-w-0 h-[46px] sm:h-[52px] rounded-[10px] sm:rounded-[12px] border-2 flex items-center justify-center ${tone}`}>
                    <span className="text-[18px] min-[360px]:text-[20px] sm:text-[22px]" style={{ ...F, fontWeight: 700, color: '#2b2b2b' }}>{ch ?? ''}</span>
                    {active && !ch ? (
                      <span className="absolute w-[2px] h-6 bg-[#3973bf] animate-pulse motion-reduce:animate-none" />
                    ) : null}
                  </div>
                );
              })}
            </div>
            <input
              ref={inputRef}
              value={code}
              onChange={(e) => { setCode(sanitize(e.target.value)); if (error) setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              maxLength={CODE_LENGTH + 4}
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={submitting}
              aria-label="Sınıf kodu"
              aria-describedby="join-class-hint"
              aria-invalid={error ? true : undefined}
              className="absolute inset-0 w-full h-full opacity-0 cursor-text"
            />
          </div>
          <p id="join-class-hint" className="sr-only">Öğretmeninin verdiği 8 karakterli sınıf kodu</p>

          {error ? (
            <p role="alert" style={{ ...F, fontSize: 13, color: '#c2185b', marginTop: 8 }}>{error}</p>
          ) : null}

          <button
            type="button"
            onClick={submit}
            disabled={!complete || submitting}
            className="mt-4 w-full h-[48px] rounded-[12px] bg-[#e5599b] hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40 flex items-center justify-center"
          >
            {submitting
              ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Sınıfa katıl</span>}
          </button>
        </div>
      </div>

      {/* ── What joining turns on — the sidebar's own icons ─────────────────── */}
      <div className="lg:pt-2">
        <h3 style={{ ...F, fontSize: 18, fontWeight: 700, color: '#2b2b2b', marginBottom: 14 }}>Sınıfına katılınca</h3>
        <ul className="space-y-4">
          {UNLOCKS.map((u) => (
            <li key={u.title} className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: u.color }}>
                <img src={u.icon} alt="" className="w-[22px] h-[22px] object-contain" />
              </span>
              <span className="min-w-0">
                <span className="block" style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>{u.title}</span>
                <span className="block" style={{ ...F, fontSize: 13, color: '#666' }}>{u.text}</span>
              </span>
            </li>
          ))}
        </ul>
        <p style={{ ...F, fontSize: 13, color: '#666', marginTop: 20, lineHeight: 1.55, maxWidth: 460 }}>
          {awaiting
            ? `Kodun yok mu? Öğretmenine sor. ${kurumName || 'Kurumun'} seni bir sınıfa yerleştirdiğinde sınıfın burada kendiliğinden görünür.`
            : 'Kodun yok mu? Öğretmenine sor.'}
        </p>
      </div>
    </div>
  );
};

export default JoinClassCard;
